import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { customersAPI } from "../../services";
import CustomerForm from "./CustomerForm";
import { Download, Check, ChevronDown } from "lucide-react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

import "../../style/customers.css";
import CreditCustomersIcon from "../../assets/credit-customers.svg";
import ActiveCustomersIcon from "../../assets/active-customer.svg";
import TotalCustomersIcon from "../../assets/total-customers.svg";
import TotalInventoryIcon from "../../assets/total-inventory.svg";
import AddCustomer from "../../assets/icons/AddCustomer";
import TrendUp from "../../assets/icons/TrendUp";
import TrendDown from "../../assets/icons/TrendDown";
import FilterIcon from "../../assets/icons/FilterIcon";
import EditIcon from "../../assets/icons/EditIcon";
import EyeIcon from "../../assets/icons/EyeIcon";
import NoDataIcon from "../../assets/icons/NoDataIcon";
import ArrowDownUpIcon from "../../assets/icons/ArrowDownUpIcon";

import FilePdf from "../../assets/file-pdf.svg";
import FileExcel from "../../assets/microsoft-excel-logo.svg";
import FileCsv from "../../assets/file-csv.svg";
import { useSelector } from "react-redux";

const Customers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isPartsURL, isAccessoriesURL, isGodownURL } = useSelector((state) => state.domain);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [currentPage, setCurrentPage] = useState(
    parseInt(searchParams.get("page")) || 1
  );
  const [itemsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0); // Total count from API
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [sortField, setSortField] = useState(searchParams.get("sortField") || "");
  const [sortDirection, setSortDirection] = useState(searchParams.get("sortDirection") || ""); // "" = original, "asc", "desc"
  const [balanceFilter, setBalanceFilter] = useState(
    searchParams.get("filter") || "all"
  );
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customerStats, setCustomerStats] = useState({
    total_customers: { count: 0, statistics: {} },
    active_customers: { count: 0, statistics: {} },
    credit_customers: { count: 0, statistics: {} },
    outstanding_balance: { count: 0, statistics: {} },
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const filterDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const isFetchingRef = useRef(false);
  const searchInputRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const tableContainerRef = useRef(null);
  const tbodyRef = useRef(null);

  // Load customers and stats from API
  const fetchCustomers = async (search = "", page = 1, type = "", sortBy = "") => {
    // Prevent concurrent calls
    if (isFetchingRef.current) {
      return;
    }
    
    isFetchingRef.current = true;
    setLoading(true);
    
    try {
      const result = await customersAPI.getCustomers(
        search,
        page,
        itemsPerPage,
        type,
        sortBy
      );
      if (result.error) {
        toast.error(result.error.message || "Failed to fetch customers");
        return;
      }

      const customersData = result.response?.data || [];
      const totalCount = result.response?.count || 0;

      setCustomers(customersData);
      setTotalCount(totalCount); // Store total count for pagination
    } catch (error) {
      console.error("Error fetching customers:", error);
      toast.error("Failed to fetch customers");
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  };

  const fetchCustomerStats = async () => {
    setStatsLoading(true);
    try {
      const result = await customersAPI.getCustomerCount();
      if (result.error) {
        console.error("Failed to fetch customer stats:", result.error.message);
        toast.error(
          result.error.message || "Failed to fetch customer statistics"
        );
        return;
      }

      // The API service now returns normalized data directly
      const stats = result.response || {};
      setCustomerStats(stats);
    } catch (error) {
      console.error("Error fetching customer stats:", error);
      toast.error("Failed to fetch customer statistics");
    } finally {
      setStatsLoading(false);
    }
  };

  // Single ref to track if component has mounted and loaded data
  const hasLoadedRef = useRef(false);
  const shouldRestoreScrollRef = useRef(false);
  const savedTableScrollRef = useRef(0);

  useEffect(() => {
    // Guard against double execution
    if (hasLoadedRef.current) {
      return;
    }
    
    // Check if we should restore scroll positions
    const savedWindowScroll = sessionStorage.getItem("customersWindowScroll");
    const savedBodyScroll = sessionStorage.getItem("customersBodyScroll");
    const savedHtmlScroll = sessionStorage.getItem("customersHtmlScroll");
    const savedTableScroll = sessionStorage.getItem("customersTableScroll");
    const savedTbodyScroll = sessionStorage.getItem("customersTbodyScroll");
    const savedParentScroll = sessionStorage.getItem("customersParentScroll");
    const savedParentClass = sessionStorage.getItem("customersParentClass");
    
    if (savedWindowScroll || savedTableScroll || savedTbodyScroll || savedParentScroll) {
      shouldRestoreScrollRef.current = true;
      scrollPositionRef.current = parseInt(savedWindowScroll || "0");
      savedTableScrollRef.current = {
        window: parseInt(savedWindowScroll || "0"),
        body: parseInt(savedBodyScroll || "0"),
        html: parseInt(savedHtmlScroll || "0"),
        table: parseInt(savedTableScroll || "0"),
        tbody: parseInt(savedTbodyScroll || "0"),
        parent: parseInt(savedParentScroll || "0"),
        parentClass: savedParentClass || ""
      };
      
      // Clean up
      sessionStorage.removeItem("customersWindowScroll");
      sessionStorage.removeItem("customersBodyScroll");
      sessionStorage.removeItem("customersHtmlScroll");
      sessionStorage.removeItem("customersTableScroll");
      sessionStorage.removeItem("customersTbodyScroll");
      sessionStorage.removeItem("customersParentScroll");
      sessionStorage.removeItem("customersParentClass");
    }
    
    // Call both functions, then mark as loaded
    const loadData = async () => {
      // Get filter type from URL params
      const type =
        balanceFilter === "positive"
          ? "credit"
          : balanceFilter === "negative"
          ? "debit"
          : "";
      
      // Get sort parameter from state
      const sortBy = sortField === "balance" && sortDirection === "asc" 
        ? "balance_asc" 
        : sortField === "balance" && sortDirection === "desc"
        ? "balance_desc"
        : "";
      
      // Fetch with URL params (page, search, filter, sort)
      await fetchCustomers(searchTerm, currentPage, type, sortBy);
      await fetchCustomerStats();
      // Only set to true after initial load completes
      hasLoadedRef.current = true;
      
      // Focus search input after data loads
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    };
    
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target)
      ) {
        setShowFilterDropdown(false);
      }
      if (
        exportDropdownRef.current &&
        !exportDropdownRef.current.contains(event.target)
      ) {
        setShowExportDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Sync sort state with URL params when they change (e.g., when navigating back)
  useEffect(() => {
    if (!hasLoadedRef.current) {
      return; // Don't sync on initial mount, let initial load handle it
    }

    const urlSortField = searchParams.get("sortField") || "";
    const urlSortDirection = searchParams.get("sortDirection") || "";
    
    // Only update if different to avoid infinite loops
    if (urlSortField !== sortField || urlSortDirection !== sortDirection) {
      setSortField(urlSortField);
      setSortDirection(urlSortDirection);
      
      // Refetch with new sort from URL
      const type =
        balanceFilter === "positive"
          ? "credit"
          : balanceFilter === "negative"
          ? "debit"
          : "";
      const sortBy = urlSortField === "balance" && urlSortDirection === "asc" 
        ? "balance_asc" 
        : urlSortField === "balance" && urlSortDirection === "desc"
        ? "balance_desc"
        : "";
      fetchCustomers(searchTerm, currentPage, type, sortBy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Update URL params when state changes
  useEffect(() => {
    if (!hasLoadedRef.current) {
      return;
    }

    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    if (currentPage > 1) params.set("page", currentPage.toString());
    if (balanceFilter !== "all") params.set("filter", balanceFilter);
    if (sortField) params.set("sortField", sortField);
    if (sortDirection) params.set("sortDirection", sortDirection);

    setSearchParams(params, { replace: true });
  }, [searchTerm, currentPage, balanceFilter, sortField, sortDirection, setSearchParams]);

  // Handle search with API call
  useEffect(() => {
    // Skip on initial mount to avoid duplicate API call
    if (!hasLoadedRef.current) {
      return;
    }

    const delayedSearch = setTimeout(() => {
      const type =
        balanceFilter === "positive"
          ? "credit"
          : balanceFilter === "negative"
          ? "debit"
          : "";
      const sortBy = sortField === "balance" && sortDirection === "asc" 
        ? "balance_asc" 
        : sortField === "balance" && sortDirection === "desc"
        ? "balance_desc"
        : "";
      setCurrentPage(1); // Reset pagination on search
      fetchCustomers(searchTerm, 1, type, sortBy); // Always call with current searchTerm
    }, 500); // Debounce search

    return () => clearTimeout(delayedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, balanceFilter, sortField, sortDirection]);

  // Set filtered customers to match API results (server-side filtering and sorting)
  useEffect(() => {
    setFilteredCustomers(customers);
  }, [customers]);

  // Separate effect for scroll restoration - runs after filteredCustomers is set
  useEffect(() => {
    if (shouldRestoreScrollRef.current && filteredCustomers.length > 0 && !loading && !statsLoading) {
      const scrollTargets = savedTableScrollRef.current;
      
      // Use multiple strategies to ensure scroll happens
      let attempts = 0;
      const maxAttempts = 20; // Try for 2 seconds (20 * 100ms)
      
      const attemptScroll = () => {
        attempts++;
        
        // Try all possible scroll methods
        if (scrollTargets.window > 0) {
          window.scrollTo(0, scrollTargets.window);
        }
        if (scrollTargets.body > 0) {
          document.body.scrollTop = scrollTargets.body;
        }
        if (scrollTargets.html > 0) {
          document.documentElement.scrollTop = scrollTargets.html;
        }
        if (scrollTargets.table > 0 && tableContainerRef.current) {
          tableContainerRef.current.scrollTop = scrollTargets.table;
        }
        if (scrollTargets.tbody > 0 && tbodyRef.current) {
          tbodyRef.current.scrollTop = scrollTargets.tbody;
        }
        if (scrollTargets.parent > 0 && scrollTargets.parentClass) {
          const parentElement = document.querySelector(`.${scrollTargets.parentClass.split(' ')[0]}`);
          if (parentElement) {
            parentElement.scrollTop = scrollTargets.parent;
          }
        }
        
        // Check current positions
        const currentWindow = window.scrollY || 0;
        const currentBody = document.body.scrollTop || 0;
        const currentHtml = document.documentElement.scrollTop || 0;
        const currentTable = tableContainerRef.current?.scrollTop || 0;
        const currentTbody = tbodyRef.current?.scrollTop || 0;
        
        // Check if any scroll position was achieved
        const anyScrollAchieved = 
          (scrollTargets.window > 0 && Math.abs(currentWindow - scrollTargets.window) < 10) ||
          (scrollTargets.body > 0 && Math.abs(currentBody - scrollTargets.body) < 10) ||
          (scrollTargets.html > 0 && Math.abs(currentHtml - scrollTargets.html) < 10) ||
          (scrollTargets.table > 0 && Math.abs(currentTable - scrollTargets.table) < 10) ||
          (scrollTargets.tbody > 0 && Math.abs(currentTbody - scrollTargets.tbody) < 10) ||
          (scrollTargets.parent > 0 && scrollTargets.parentClass);
        
        if (anyScrollAchieved || attempts >= maxAttempts) {
          shouldRestoreScrollRef.current = false;
          return true;
        }
        
        return false;
      };
      
      // Start attempting after a short delay
      const initialDelay = setTimeout(() => {
        const intervalId = setInterval(() => {
          if (attemptScroll()) {
            clearInterval(intervalId);
          }
        }, 100);
        
        // Cleanup interval after max time
        setTimeout(() => clearInterval(intervalId), 2500);
      }, 50);

      return () => clearTimeout(initialDelay);
    }
  }, [filteredCustomers, loading, statsLoading]);

  const handleSort = (field) => {
    let newSortField = "";
    let newSortDirection = "";

    if (sortField === field) {
      // Cycle through: asc -> desc -> original
      if (sortDirection === "asc") {
        newSortDirection = "desc";
        newSortField = field;
      } else if (sortDirection === "desc") {
        newSortDirection = "";
        newSortField = "";
      }
    } else {
      newSortField = field;
      newSortDirection = "asc";
    }

    setSortField(newSortField);
    setSortDirection(newSortDirection);

    // Fetch with new sort parameter
    const type =
      balanceFilter === "positive"
        ? "credit"
        : balanceFilter === "negative"
        ? "debit"
        : "";
    const sortBy = newSortField === "balance" && newSortDirection === "asc" 
      ? "balance_asc" 
      : newSortField === "balance" && newSortDirection === "desc"
      ? "balance_desc"
      : "";
    fetchCustomers(searchTerm, currentPage, type, sortBy);
  };

  const handleAddCustomer = async (customerData) => {
    try {
      const result = await customersAPI.createCustomer(customerData);

      if (result.error) {
        console.error("Backend error creating customer:", result.error);
        return result; // Return error to form for handling
      }

      toast.success("Customer added successfully!");
      setShowForm(false);
      const type =
        balanceFilter === "positive"
          ? "credit"
          : balanceFilter === "negative"
          ? "debit"
          : "";
      const sortBy = sortField === "balance" && sortDirection === "asc" 
        ? "balance_asc" 
        : sortField === "balance" && sortDirection === "desc"
        ? "balance_desc"
        : "";
      fetchCustomers(searchTerm, currentPage, type, sortBy); // Refresh the list
      fetchCustomerStats(); // Refresh stats

      return result;
    } catch (error) {
      console.error("Error adding customer:", error);
      return {
        error: {
          message: "Failed to add customer",
          originalError: error,
        },
      };
    }
  };

  const handleEditCustomer = async (customerData) => {
    try {
      const result = await customersAPI.updateCustomer(
        editingCustomer.id,
        customerData
      );

      if (result.error) {
        console.error("Backend error updating customer:", result.error);
        return result; // Return error to form for handling
      }

      toast.success("Customer updated successfully!");
      setShowForm(false);
      setEditingCustomer(null);
      const type =
        balanceFilter === "positive"
          ? "credit"
          : balanceFilter === "negative"
          ? "debit"
          : "";
      const sortBy = sortField === "balance" && sortDirection === "asc" 
        ? "balance_asc" 
        : sortField === "balance" && sortDirection === "desc"
        ? "balance_desc"
        : "";
      fetchCustomers(searchTerm, currentPage, type, sortBy); // Refresh the list
      fetchCustomerStats(); // Refresh stats

      return result;
    } catch (error) {
      console.error("Error updating customer:", error);
      return {
        error: {
          message: "Failed to update customer",
          originalError: error,
        },
      };
    }
  };

  const openEditForm = (customer) => {
    // Save current scroll position in case form is cancelled
    scrollPositionRef.current = window.scrollY;
    setEditingCustomer(customer);
    setShowForm(true);
  };

  const openViewPage = (customer) => {
    // Find which element is actually scrolling
    const windowScroll = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
    const bodyScroll = document.body.scrollTop;
    const htmlScroll = document.documentElement.scrollTop;
    const tableScroll = tableContainerRef.current?.scrollTop || 0;
    const tbodyScroll = tbodyRef.current?.scrollTop || 0;
    
    // Check for scrollable parent containers
    let scrollableParent = null;
    let scrollableParentScroll = 0;
    let element = tableContainerRef.current?.parentElement;
    
    while (element && element !== document.body) {
      if (element.scrollTop > 0) {
        scrollableParent = element;
        scrollableParentScroll = element.scrollTop;
        break;
      }
      element = element.parentElement;
    }
    
    // Save all possible scroll positions
    sessionStorage.setItem("customersWindowScroll", windowScroll.toString());
    sessionStorage.setItem("customersBodyScroll", bodyScroll.toString());
    sessionStorage.setItem("customersHtmlScroll", htmlScroll.toString());
    sessionStorage.setItem("customersTableScroll", tableScroll.toString());
    sessionStorage.setItem("customersTbodyScroll", tbodyScroll.toString());
    sessionStorage.setItem("customersParentScroll", scrollableParentScroll.toString());
    sessionStorage.setItem("customersParentClass", scrollableParent?.className || "");
    
    // Preserve current state in URL when navigating to details
    const params = new URLSearchParams();
    if (searchTerm) params.set("search", searchTerm);
    if (currentPage > 1) params.set("page", currentPage.toString());
    if (balanceFilter !== "all") params.set("filter", balanceFilter);
    if (sortField) params.set("sortField", sortField);
    if (sortDirection) params.set("sortDirection", sortDirection);
    
    // Build the full return URL and encode it to preserve query parameters
    const returnUrl = `/customers${params.toString() ? `?${params.toString()}` : ""}`;
    navigate(`/customer/${customer.id}?returnTo=${encodeURIComponent(returnUrl)}`);
  };

  // Use API statistics only
  const totalCustomers = customerStats.total_customers?.count ?? 0;
  const activeCustomers = customerStats.active_customers?.count ?? 0;
  const creditCustomers = customerStats.credit_customers?.count ?? 0;
  const outstandingBalance = customerStats.outstanding_balance?.count ?? 0;

  // Format numbers for display
  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
    }).format(amount || 0);
  };

  // Helper function to get trend percentage
  const getTrendPercentage = (statistics) => {
    if (
      statistics?.change_percent === null ||
      statistics?.change_percent === undefined
    ) {
      return "--";
    }
    return `${parseFloat(statistics.change_percent).toFixed(1)}%`;
  };

  // Helper function to get trend direction
  const getTrendDirection = (statistics) => {
    const trend = statistics?.trend;
    if (trend === "increase") return "up";
    if (trend === "decrease") return "down";
    return "no_change";
  };

  // Pagination - Server-side pagination, no slicing needed
  const currentCustomers = filteredCustomers;
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  // Show pagination if there are items or multiple pages
  const shouldShowPagination = totalCount > 25 && totalPages > 1;

  const SortableHeader = ({ field, children }) => (
    <th
      className="sortable-header"
      onClick={() => handleSort(field)}
      style={{ cursor: "pointer" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
        {children}
        <ArrowDownUpIcon 
          sortDirection={sortField === field ? sortDirection : null}
          color={sortField === field ? isPartsURL ? 'var(--primary-color-parts)' : (isAccessoriesURL || isGodownURL) ? 'var(--primary-color-acc)' : 'var(--primary-color-mobile)' : '#57606A'}
        />
      </div>
    </th>
  );

  const formatBalance = (balance) => {
    const amount = parseFloat(balance || 0);
    return `€${amount.toFixed(2)}`;
  };

  const filterOptions = [
    { value: "all", label: "All Balance" },
    { value: "positive", label: "Credit" },
    { value: "negative", label: "Debit" },
  ];

  const handleFilterSelect = (value) => {
    setBalanceFilter(value);
    setShowFilterDropdown(false);
    setCurrentPage(1); // Reset to page 1 when filter changes

    // Trigger API call with new filter
    const type =
      value === "positive" ? "credit" : value === "negative" ? "debit" : "";
    const sortBy = sortField === "balance" && sortDirection === "asc" 
      ? "balance_asc" 
      : sortField === "balance" && sortDirection === "desc"
      ? "balance_desc"
      : "";
    fetchCustomers(searchTerm, 1, type, sortBy);
  };

  const getFilterLabel = () => {
    const option = filterOptions.find((opt) => opt.value === balanceFilter);
    return option ? option.label : "All Balance";
  };

  const exportOptions = [
    { value: "pdf", label: "PDF", icon: FilePdf },
    { value: "excel", label: "Excel", icon: FileExcel },
    { value: "csv", label: "CSV", icon: FileCsv },
  ];

  // Fetch all customers for export (without pagination)
  const fetchAllCustomersForExport = async () => {
    try {
      const type =
        balanceFilter === "positive"
          ? "credit"
          : balanceFilter === "negative"
          ? "debit"
          : "";
      const result = await customersAPI.getCustomers(searchTerm, 1, 9999, type);
      if (result.error) {
        toast.error(
          result.error.message || "Failed to fetch customers for export"
        );
        return [];
      }
      return result.response?.data || [];
    } catch (error) {
      console.error("Error fetching customers for export:", error);
      toast.error("Failed to fetch customers for export");
      return [];
    }
  };

  const handleExportSelect = async (value) => {
    setShowExportDropdown(false);

    // Fetch all customers for export
    const allCustomers = await fetchAllCustomersForExport();

    if (allCustomers.length === 0) {
      toast.warning("No customers to export");
      return;
    }

    switch (value) {
      case "pdf":
        // Navigate to CustomerPrint page with customer data
        navigate("/customers/print", {
          state: { customers: allCustomers },
        });
        break;
      case "excel":
        exportToExcel(allCustomers);
        break;
      case "csv":
        exportToCSV(allCustomers);
        break;
      default:
        toast.warning(`Export format ${value} not supported`);
    }
  };

  // Helper function to safely get field value
  const getFieldValue = (value, defaultValue = "") => {
    if (value === null || value === undefined || value === "") {
      return defaultValue;
    }
    return String(value);
  };
  const exportToExcel = (customersToExport) => {
    // Prepare data for Excel with ID as first column, removing Status
    const excelData = customersToExport.map((customer) => ({
      ID: getFieldValue(customer.id),
      Name: getFieldValue(customer.name),
      Email: getFieldValue(customer.user_email || customer.email),
      Phone: getFieldValue(customer.mobile || customer.phone),
      Address: getFieldValue(customer.address),
      Balance: getFieldValue(customer.balance, "0"),
    }));

    // Create workbook and worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Customers");

    // Set column widths
    ws["!cols"] = [
      { wch: 10 }, // ID
      { wch: 20 }, // Name
      { wch: 30 }, // Email
      { wch: 15 }, // Phone
      { wch: 40 }, // Address
      { wch: 12 }, // Balance
    ];

    // Apply color formatting to Balance column (column F, index 5)
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let row = 1; row <= range.e.r; row++) {
      // Skip header row (row 0), start from row 1
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: 5 }); // Column F (Balance)
      if (ws[cellAddress]) {
        const customer = customersToExport[row - 1];
        if (customer) {
          // Handle string balance like "-90.00" or numeric balance
          const balanceValue = customer.balance;
          const balance =
            typeof balanceValue === "string"
              ? parseFloat(balanceValue.replace(/[€,\s]/g, "")) || 0
              : parseFloat(balanceValue || 0);
          const color = balance >= 0 ? "#0a8900" : "#dc2626";
          if (!ws[cellAddress].s) ws[cellAddress].s = {};
          if (!ws[cellAddress].s.font) ws[cellAddress].s.font = {};
          ws[cellAddress].s.font.color = { rgb: color.substring(1) }; // Remove # for Excel
        }
      }
    }

    // Save the Excel file
    XLSX.writeFile(wb, "customers-report.xlsx");
  };

  const exportToCSV = (customersToExport) => {
    // Prepare CSV data with ID as first column, removing Status
    const csvHeaders = ["ID", "Name", "Email", "Phone", "Address", "Balance"];
    const csvData = customersToExport.map((customer) => [
      getFieldValue(customer.id),
      getFieldValue(customer.name),
      getFieldValue(customer.user_email || customer.email),
      getFieldValue(customer.mobile || customer.phone),
      getFieldValue(customer.address),
      getFieldValue(customer.balance, "0"),
    ]);

    // Combine headers and data
    const csvContent = [csvHeaders, ...csvData]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "customers-report.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {/* Content Area */}
      <div className="card rounded-3 p-4 border-0 mb-1 customers-container">
        <div className="customers-layout">
          {/* Header */}
          <div className="customers-top">
            <div className="customers-header">
              <div>
                <h2 className="customers-title">Customers</h2>
                <p className="customers-subtitle">Manage your customers</p>
              </div>

              <div className="d-flex gap-2 align-items-center">
                {/* Add Customer Button */}
                <button
                  className={`${
                    isPartsURL
                      ? "add-customer-btn-parts"
                      : (isAccessoriesURL || isGodownURL)
                      ? "add-customer-btn-acc"
                      : "add-customer-btn"
                  }`}
                  onClick={() => {
                    scrollPositionRef.current = window.scrollY;
                    setShowForm(true);
                  }}
                >
                  <AddCustomer /> Add Customer
                </button>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="row mb-2">
              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Total Customers
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(totalCustomers)
                        )}
                      </h4>

                      {/* Trend badge */}
                      <div className="trend-badge">
                        <div className="trend-badge-content">
                          {getTrendDirection(
                            customerStats.total_customers?.statistics
                          ) === "up" ? (
                            <TrendUp size={12} className="trend-badge-icon" />
                          ) : getTrendDirection(
                              customerStats.total_customers?.statistics
                            ) === "down" ? (
                            <TrendDown size={12} className="trend-badge-icon" />
                          ) : (
                            <span className="trend-badge-icon">—</span>
                          )}
                          <p className="trend-badge-percentage">
                            {getTrendPercentage(
                              customerStats.total_customers?.statistics
                            )}
                          </p>
                        </div>
                        <p className="trend-badge-text">From last week</p>
                      </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img src={TotalCustomersIcon} alt="Total Brands" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Active Customers
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(activeCustomers)
                        )}
                      </h4>

                      {/* Trend badge */}
                      <div className="trend-badge">
                        <div className="trend-badge-content">
                          {getTrendDirection(
                            customerStats.active_customers?.statistics
                          ) === "up" ? (
                            <TrendUp size={12} className="trend-badge-icon" />
                          ) : getTrendDirection(
                              customerStats.active_customers?.statistics
                            ) === "down" ? (
                            <TrendDown size={12} className="trend-badge-icon" />
                          ) : (
                            <span className="trend-badge-icon">—</span>
                          )}
                          <p className="trend-badge-percentage">
                            {getTrendPercentage(
                              customerStats.active_customers?.statistics
                            )}
                          </p>
                        </div>
                        <p className="trend-badge-text">From last week</p>
                      </div>
                    </div>

                    <div className="d-flex align-items-center justify-content-center">
                      <img src={ActiveCustomersIcon} alt="Total Products" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Credit Customers
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatNumber(creditCustomers)
                        )}
                      </h4>

                      {/* Trend badge */}
                      <div className="trend-badge">
                        <div className="trend-badge-content">
                          {getTrendDirection(
                            customerStats.credit_customers?.statistics
                          ) === "up" ? (
                            <TrendUp size={12} className="trend-badge-icon" />
                          ) : getTrendDirection(
                              customerStats.credit_customers?.statistics
                            ) === "down" ? (
                            <TrendDown size={12} className="trend-badge-icon" />
                          ) : (
                            <span className="trend-badge-icon">—</span>
                          )}
                          <p className="trend-badge-percentage">
                            {getTrendPercentage(
                              customerStats.credit_customers?.statistics
                            )}
                          </p>
                        </div>
                        <p className="trend-badge-text">From last week</p>
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-center">
                      <img src={CreditCustomersIcon} alt="Total Brands" />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-md-3">
                <div
                  className="card brands-summary-card"
                  style={{
                    border: "1px solid #DCE3EB",
                    borderRadius: "10px",
                  }}
                >
                  <div className="card-body text-center d-flex align-items-start justify-content-between">
                    <div className="d-flex flex-column align-items-start justify-content-center mb-2 gap-2">
                      <h6
                        className="mb-0 text-muted"
                        style={{
                          fontSize: "14px",
                          color: "#6E7781",
                          fontWeight: "400",
                        }}
                      >
                        Outstanding Balance
                      </h6>
                      <h4
                        className="mb-0 fw-semibold"
                        style={{
                          fontSize: "22px",
                          color: "#424A53",
                          fontWeight: "500",
                        }}
                      >
                        {statsLoading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                        ) : (
                          formatCurrency(outstandingBalance)
                        )}
                      </h4>

                      {/* Trend badge */}
                      <div className="trend-badge">
                        <div className="trend-badge-content">
                          {getTrendDirection(
                            customerStats.outstanding_balance?.statistics
                          ) === "up" ? (
                            <TrendUp size={12} className="trend-badge-icon" />
                          ) : getTrendDirection(
                              customerStats.outstanding_balance?.statistics
                            ) === "down" ? (
                            <TrendDown size={12} className="trend-badge-icon" />
                          ) : (
                            <span className="trend-badge-icon">—</span>
                          )}
                          <p className="trend-badge-percentage">
                            {getTrendPercentage(
                              customerStats.outstanding_balance?.statistics
                            )}
                          </p>
                        </div>
                        <p className="trend-badge-text">From last week</p>
                      </div>
                    </div>
                    <div className="d-flex align-items-center justify-content-center">
                      <img src={TotalInventoryIcon} alt="Total Products" />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search + Balance Filter + Export Filter */}
            <div className="search-filter-section mb-4">
              {/* Search Bar */}
              <div className="customers-search-container">
                <input
                  ref={searchInputRef}
                  type="text"
                  className={`${
                    isPartsURL
                      ? "customers-search-input-parts"
                      : (isAccessoriesURL || isGodownURL)
                      ? "customers-search-input-acc"
                      : "customers-search-input"
                  }`}
                  placeholder="Search customers by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <img
                  src="/icons/Search.png"
                  alt="Search"
                  className="customers-search-icon"
                />
              </div>

              {/* Balance Filter */}
              <div className="customer-filter-dropdown" ref={filterDropdownRef}>
                <button
                  className={`${
                    isPartsURL
                      ? "customers-filter-button-parts"
                      : isAccessoriesURL
                      ? "customers-filter-button-acc"
                      : "customers-filter-button"
                  }`}
                  onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                >
                  <FilterIcon size={16} />
                  {getFilterLabel()}
                </button>

                <div
                  className={`customer-filter-dropdown-content ${
                    showFilterDropdown ? "show" : ""
                  }`}
                >
                  {filterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`customer-filter-dropdown-item ${
                        balanceFilter === option.value ? "selected" : ""
                      }`}
                      onClick={() => handleFilterSelect(option.value)}
                    >
                      <Check
                        size={16}
                        className="customer-filter-dropdown-check"
                      />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* Export Filter */}
              <div className="customer-filter-dropdown" ref={exportDropdownRef}>
                <button
                  className={`${
                    isPartsURL
                      ? "customers-filter-button-parts"
                      : isAccessoriesURL
                      ? "customers-filter-button-acc"
                      : "customers-filter-button"
                  }`}
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                >
                  <Download size={16} />
                  Export
                  <ChevronDown
                    size={12}
                    className={`customer-export-dropdown-arrow ${
                      showExportDropdown ? "rotated" : ""
                    }`}
                  />
                </button>

                <div
                  className={`customer-export-dropdown-content ${
                    showExportDropdown ? "show" : ""
                  }`}
                >
                  {exportOptions.map((option) => (
                    <div
                      key={option.value}
                      className="customer-export-dropdown-item"
                      onClick={() => handleExportSelect(option.value)}
                    >
                      <img
                        src={option.icon}
                        alt={option.label}
                        width="16"
                        height="16"
                      />
                      {option.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Customers Table */}
          <div className="customers-table-wrapper" ref={tableContainerRef}>
            <div className="customer-table-responsive">
              <table className="customer-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>NAME</th>
                    <th>EMAIL</th>
                    <th>PHONE</th>
                    <th>ADDRESS</th>
                    <SortableHeader field="balance">BALANCE</SortableHeader>
                    <th style={{ textAlign: "center" }}>ACTION</th>
                  </tr>
                </thead>
                <tbody ref={tbodyRef}>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <h3 style={{ marginTop: "2rem" }}>
                            Loading customers...
                          </h3>
                        </div>
                      </td>
                    </tr>
                  ) : currentCustomers.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="no-products-message">
                        <div
                          className=""
                          style={{
                            marginBottom: "4rem",
                            marginTop: "4rem",
                          }}
                        >
                          <NoDataIcon />
                          <h3 style={{ marginTop: "2rem" }}>
                            No customers found
                          </h3>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    currentCustomers.map((customer) => (
                      <tr key={customer.id}>
                        <td>{customer.id}</td>
                        <td
                          title={customer.name}
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {customer.name}
                        </td>
                        <td
                          title={customer.user_email || customer.email}
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {customer.user_email || customer.email}
                        </td>
                        <td>{customer.mobile || customer.phone}</td>
                        <td
                          title={customer.address}
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {customer.address}
                        </td>
                        <td>
                          <span
                            className={`balance-badge ${
                              (customer.balance || 0) >= 0
                                ? "positive"
                                : "negative"
                            }`}
                          >
                            {formatBalance(customer.balance)}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className={`${
                                isPartsURL
                                  ? "action-btn-parts view"
                                  : isAccessoriesURL
                                  ? "action-btn-acc view"
                                  : "action-btn view"
                              }`}
                              onClick={() => openViewPage(customer)}
                              title="View customer"
                            >
                              <EyeIcon
                                fill={
                                  isPartsURL
                                    ? "var(--primary-color-parts)"
                                    : isAccessoriesURL
                                    ? "var(--primary-color-acc)"
                                    : "var(--primary-color-mobile)"
                                }
                              />
                            </button>
                            <button
                              className={`${
                                isPartsURL
                                  ? "action-btn-parts view"
                                  : isAccessoriesURL
                                  ? "action-btn-acc view"
                                  : "action-btn view"
                              }`}
                              onClick={() => openEditForm(customer)}
                              title="Edit customer"
                            >
                              <EditIcon
                                fill={
                                  isPartsURL
                                    ? "var(--primary-color-parts)"
                                    : isAccessoriesURL
                                    ? "var(--primary-color-acc)"
                                    : "var(--primary-color-mobile)"
                                }
                              />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {shouldShowPagination && (
            <div className="customers-pagination-sticky">
              <div className="customer-pagination-container">
                <div className="customer-pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="customer-pagination-buttons">
                  <button
                    className={`${
                      isPartsURL
                        ? "customer-pagination-btn-parts"
                        : isAccessoriesURL
                        ? "customer-pagination-btn-acc"
                        : "customer-pagination-btn"
                    }`}
                    onClick={() => {
                      const newPage = currentPage - 1;
                      setCurrentPage(newPage);
                      const type =
                        balanceFilter === "positive"
                          ? "credit"
                          : balanceFilter === "negative"
                          ? "debit"
                          : "";
                      const sortBy = sortField === "balance" && sortDirection === "asc" 
                        ? "balance_asc" 
                        : sortField === "balance" && sortDirection === "desc"
                        ? "balance_desc"
                        : "";
                      fetchCustomers(searchTerm, newPage, type, sortBy);
                    }}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <button
                    className={`${
                      isPartsURL
                        ? "customer-pagination-btn-parts"
                        : isAccessoriesURL
                        ? "customer-pagination-btn-acc"
                        : "customer-pagination-btn"
                    }`}
                    onClick={() => {
                      const newPage = currentPage + 1;
                      setCurrentPage(newPage);
                      const type =
                        balanceFilter === "positive"
                          ? "credit"
                          : balanceFilter === "negative"
                          ? "debit"
                          : "";
                      const sortBy = sortField === "balance" && sortDirection === "asc" 
                        ? "balance_asc" 
                        : sortField === "balance" && sortDirection === "desc"
                        ? "balance_desc"
                        : "";
                      fetchCustomers(searchTerm, newPage, type, sortBy);
                    }}
                    disabled={currentPage >= totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Customer Form Modal */}
          {showForm && (
            <CustomerForm
              customer={editingCustomer}
              onSave={editingCustomer ? handleEditCustomer : handleAddCustomer}
              onCancel={() => {
                setShowForm(false);
                setEditingCustomer(null);
                // Restore scroll position when form is cancelled
                setTimeout(() => {
                  window.scrollTo(0, scrollPositionRef.current);
                }, 100);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default Customers;
